import React from 'react';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { PersonComponent } from '../personal-details/person';
import { ContactComponent } from '../personal-details/contact';
import {
  GoACallout,
  GoADivider,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAInput,
} from '@abgov/react-components';
import styles from './passenger-card.module.css';
import { removeAllValidationErrorNotifications } from '../../state/slices/notificationSlice';
import { PositionInVehicle } from '../../state/slices/configurationSlice';

interface PassengerCardProps {
  id: number;
  setId: (value: number) => void;
  firstName: string;
  setFirstName: (value: string) => void;
  lastName: string;
  setLastName: (value: string) => void;
  sex: string;
  setSex: (value: string) => void;
  dateOfBirth: string;
  setDateOfBirth: (value: string) => void;
  address: string;
  setAddress: (value: string) => void;
  city: string;
  setCity: (value: string) => void;
  country: string;
  setCountry: (value: string) => void;
  postalCode: string;
  setPostalCode: (value: string) => void;
  province: string;
  setProvince: (value: string) => void;
  primaryPhone: string;
  setPrimaryPhone: (value: string) => void;
  alternatePhone: string;
  setAlternatePhone: (value: string) => void;
  locationInVehicle: string;
  setLocationInVehicle: (value: string) => void;
  specifyLocation: string;
  setSpecifyLocation: (value: string) => void;
  safetyEquipmentUsed: string;
  setSafetyEquipmentUsed: (value: string) => void;
  specifySafetyEquipmentUsed: string;
  setSpecifySafetyEquipmentUsed: (value: string) => void;
  showSpecifySafetyEquipmentUsed: boolean;
  setShowSpecifySafetyEquipmentUsed: (value: boolean) => void;
  locationInVehicleError: boolean;
  setLocationInVehicleError: (value: boolean) => void;
  safetyEquipmentUsedError: boolean;
  setSafetyEquipmentUsedError: (value: boolean) => void;
  showSpecifyLocation: boolean;
  setShowSpecifyLocation: (value: boolean) => void;
  firstNameErrorMessage: string;
  setFirstNameErrorMessage: (value: string) => void;
  lastNameErrorMessage: string;
  setLastNameErrorMessage: (value: string) => void;
  dateOfBirthErrorMessage: string;
  setDateOfBirthErrorMessage: (value: string) => void;
  postalCodeErrorMessage: string;
  setPostalCodeErrorMessage: (value: string) => void;
  primaryPhoneErrorMessage: string;
  setPrimaryPhoneErrorMessage: (value: string) => void;
  alternatePhoneErrorMessage: string;
  setAlternatePhoneErrorMessage: (value: string) => void;
}

export const PassengerCardComponent = ({
  id,
  setId,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  sex,
  setSex,
  dateOfBirth,
  setDateOfBirth,
  address,
  setAddress,
  city,
  setCity,
  country,
  setCountry,
  postalCode,
  setPostalCode,
  province,
  setProvince,
  primaryPhone,
  setPrimaryPhone,
  alternatePhone,
  setAlternatePhone,
  locationInVehicle,
  setLocationInVehicle,
  specifyLocation,
  setSpecifyLocation,
  showSpecifyLocation,
  setShowSpecifyLocation,
  safetyEquipmentUsed,
  setSafetyEquipmentUsed,
  specifySafetyEquipmentUsed,
  setSpecifySafetyEquipmentUsed,
  showSpecifySafetyEquipmentUsed,
  setShowSpecifySafetyEquipmentUsed,
  locationInVehicleError,
  setLocationInVehicleError,
  safetyEquipmentUsedError,
  setSafetyEquipmentUsedError,
  firstNameErrorMessage,
  setFirstNameErrorMessage,
  lastNameErrorMessage,
  setLastNameErrorMessage,
  dateOfBirthErrorMessage,
  setDateOfBirthErrorMessage,
  postalCodeErrorMessage,
  setPostalCodeErrorMessage,
  primaryPhoneErrorMessage,
  setPrimaryPhoneErrorMessage,
  alternatePhoneErrorMessage,
  setAlternatePhoneErrorMessage,
}: PassengerCardProps) => {
  const dispatch = useDispatch<AppDispatch>();

  const locationInVehicleOtherSpecifyValue = 82;
  const safetyEquipmentUsedOtherSpecifyValue = 92;

  const stateConfiguration = useSelector(
    (state: RootState) => state.configuration,
  );

  const handleSafetyEquipmentUsedChange = (value: string) => {
    if (value) {
      let selectedValue = parseInt(value.toString());
      if (selectedValue === safetyEquipmentUsedOtherSpecifyValue) {
        setShowSpecifySafetyEquipmentUsed(true);
        setSafetyEquipmentUsed(value.toString());
        setSafetyEquipmentUsedError(false);
      } else {
        setShowSpecifySafetyEquipmentUsed(false);
        setSpecifySafetyEquipmentUsed('');
        setSafetyEquipmentUsed(value.toString());
        setSafetyEquipmentUsedError(false);
      }
      dispatch(removeAllValidationErrorNotifications());
    }
  };

  const handleLocationInVehicleChange = (value: string) => {
    if (value) {
      let selectedValue = parseInt(value.toString());
      if (selectedValue === locationInVehicleOtherSpecifyValue) {
        setShowSpecifyLocation(true);
        setLocationInVehicle(value.toString());
        setLocationInVehicleError(false);
      } else {
        setShowSpecifyLocation(false);
        setSpecifyLocation('');
        setLocationInVehicle(value.toString());
        setLocationInVehicleError(false);
      }
      dispatch(removeAllValidationErrorNotifications());
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--goa-space-m)',
      }}
    >
      <div>
        <GoADivider mt="l" mb="l"></GoADivider>
      </div>
      <h3 className={styles.h3}>Passenger {id} information</h3>

      <GoAFormItem
        label="Seat/location in vehicle"
        labelSize="regular"
        mt="l"
        error={
          locationInVehicleError ? `Select Passenger ${id} Seat/location` : ''
        }
      >
        <GoADropdown
          name="locationInVehicle"
          value={locationInVehicle}
          width="343px"
          error={locationInVehicleError}
          relative={true}
          onChange={(_, value) => {
            handleLocationInVehicleChange(value.toString());
          }}
          placeholder="—Select—"
        >
          {stateConfiguration.configuration.positionInVehicle
            .filter((item) => item.value != PositionInVehicle.LEFT_FRONT)
            .map(({ id, value }: { id: number; value: string }) => {
              return (
                <GoADropdownItem
                  key={id}
                  value={id.toString()}
                  label={value}
                ></GoADropdownItem>
              );
            })}
        </GoADropdown>
      </GoAFormItem>
      {showSpecifyLocation && (
        <GoAFormItem
          label="Specify seat/location"
          labelSize="regular"
          helpText="Describe the passenger’s seat or location"
          mt="m"
        >
          <GoAInput
            type="text"
            width="343px"
            maxLength={50}
            name="specifyLocation"
            value={specifyLocation}
            onChange={(_, value) => {
              if (value) {
                setSpecifyLocation(value);
              }
            }}
          ></GoAInput>
        </GoAFormItem>
      )}
      <GoAFormItem
        label="Safety equipment used"
        labelSize="regular"
        helpText="Select airbag option(s) only if it was deployed"
        mt="m"
        error={
          safetyEquipmentUsedError
            ? `Select Passenger ${id} Safety equipment used`
            : ''
        }
      >
        <GoADropdown
          name="safetyEquipmentUsedSelection"
          value={safetyEquipmentUsed}
          width="343px"
          onChange={(_, value) => {
            handleSafetyEquipmentUsedChange(value.toString());
          }}
          placeholder="—Select—"
          error={safetyEquipmentUsedError}
          relative={true}
        >
          {stateConfiguration.configuration.safetyEquipment.map((x) => {
            return (
              <GoADropdownItem
                key={x.id}
                value={x.id.toString()}
                label={x.formLabel}
              ></GoADropdownItem>
            );
          })}
        </GoADropdown>
      </GoAFormItem>
      {showSpecifySafetyEquipmentUsed && (
        <GoAFormItem
          label="Specify safety equipment used"
          labelSize="regular"
          helpText="Describe the passenger’s safety equipment"
          mt="m"
        >
          <GoAInput
            type="text"
            width="343px"
            name="specifySafetyEquipmentUsed"
            value={specifySafetyEquipmentUsed}
            maxLength={50}
            onChange={(_, value) => {
              if (value) {
                setSpecifySafetyEquipmentUsed(value);
              }
            }}
          ></GoAInput>
        </GoAFormItem>
      )}
      <GoACallout type="information" mt="m" mb="m">
        Fill out as much information as you can about this passenger.
      </GoACallout>
      <div style={{ paddingBottom: 'var(--goa-space-2xl)' }}>
        <PersonComponent
          firstName={firstName}
          setFirstName={setFirstName}
          lastName={lastName}
          setLastName={setLastName}
          sex={sex}
          setSex={setSex}
          dateOfBirth={dateOfBirth}
          setDateOfBirth={setDateOfBirth}
          firstNameErrorMessage={firstNameErrorMessage}
          setFirstNameErrorMessage={setFirstNameErrorMessage}
          lastNameErrorMessage={lastNameErrorMessage}
          setLastNameErrorMessage={setLastNameErrorMessage}
          dateOfBirthErrorMessage={dateOfBirthErrorMessage}
          setDateOfBirthErrorMessage={setDateOfBirthErrorMessage}
        />
      </div>
      <div
        style={{
          marginTop: 'var(--goa-space-xl)',
          paddingBottom: 'var(--goa-space-xl)',
        }}
      >
        <ContactComponent
          address={address}
          setAddress={setAddress}
          city={city}
          setCity={setCity}
          country={country}
          setCountry={setCountry}
          postalCode={postalCode}
          setPostalCode={setPostalCode}
          province={province}
          setProvince={setProvince}
          primaryPhone={primaryPhone}
          setPrimaryPhone={setPrimaryPhone}
          alternatePhone={alternatePhone}
          setAlternatePhone={setAlternatePhone}
          postalCodeErrorMessage={postalCodeErrorMessage}
          setPostalCodeErrorMessage={setPostalCodeErrorMessage}
          primaryPhoneErrorMessage={primaryPhoneErrorMessage}
          setPrimaryPhoneErrorMessage={setPrimaryPhoneErrorMessage}
          alternatePhoneErrorMessage={alternatePhoneErrorMessage}
          setAlternatePhoneErrorMessage={setAlternatePhoneErrorMessage}
        />
      </div>
    </div>
  );
};
